import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { AxiosError } from "axios";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import type { User } from "@/types/auth";

interface Props {
  /** Interim login token from the MFA challenge (NOT an auth token). */
  mfaToken: string;
  /** Called with the authenticated user once verification succeeds. */
  onVerified: (user: User) => void;
  /** Return to the credentials form, discarding the challenge. */
  onBack: () => void;
}

const inputCls =
  "rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-shadow focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand-primary";

/**
 * Login MFA challenge: prompts for a 6-digit TOTP code, with a toggle to enter
 * a recovery code instead (which widens the validation). Completes login via
 * the auth store's `verifyMfa`. Wrong codes show an inline error (never a
 * toast) and allow retry.
 */
export function MfaChallengeForm({ mfaToken, onVerified, onBack }: Props) {
  const { t } = useTranslation();
  const verifyMfa = useAuthStore((s) => s.verifyMfa);
  const [useRecovery, setUseRecovery] = useState(false);

  // Recovery codes are lenient (backend normalizes hyphen/space/case, min 8
  // alphanumerics); TOTP codes are exactly 6 digits.
  const schema = useRecovery
    ? z.object({
        code: z
          .string()
          .transform((v) => v.replace(/[\s-]/g, ""))
          .pipe(
            z
              .string()
              .min(8, t("mfa.recoveryInvalid"))
              .regex(/^[A-Za-z0-9]+$/, t("mfa.recoveryInvalid")),
          ),
      })
    : z.object({
        code: z
          .string()
          .min(6, t("mfa.codeInvalid"))
          .max(6, t("mfa.codeInvalid"))
          .regex(/^\d{6}$/, t("mfa.codeInvalid")),
      });

  type FormValues = { code: string };

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const user = await verifyMfa(mfaToken, values.code);
      onVerified(user);
    } catch (err) {
      const axiosErr = err as AxiosError<Record<string, string[] | string>>;
      const data = axiosErr.response?.data;
      // DELIBERATELY GENERIC — do not "improve" this to surface the server's
      // reason the way the authenticated MFA forms now do (TASK-4747).
      //
      // This is the UNAUTHENTICATED login step. The backend answers every
      // failure here with one generic 401 on purpose: a specific reason would
      // confirm to an anonymous caller both that the account exists and that a
      // presented recovery code is real. In particular the backend never emits
      // MFA_RECOVERY_CODE_ALREADY_USED on this endpoint — only on the
      // authenticated /disable/ and /recovery-codes/regenerate/ — so there is no
      // specific reason to surface even if this branch wanted one.
      // Owner decision 2026-08-07; see `apps/core/error_codes.py`.
      if (data) {
        setError("code", { message: t("mfa.codeIncorrect") });
        return;
      }
      // No response body at all (network/CORS). Not a code failure, so it gets
      // the platform-wide generic rather than the deleted `mfa.genericError`
      // placeholder, which told the user to "try again" for every cause alike.
      setError("code", { message: t("common.errorOccurred") });
    }
  };

  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold text-gray-900">
        {t("mfa.challengeTitle")}
      </h1>
      <p className="mb-7 text-sm text-gray-400">
        {useRecovery ? t("mfa.recoveryTitle") : t("mfa.enterCode")}
      </p>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="flex flex-col gap-5"
      >
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="mfa-code"
            className="text-sm font-medium text-gray-700"
          >
            {useRecovery ? t("mfa.recoveryTitle") : t("mfa.enterCode")}
          </label>
          <input
            id="mfa-code"
            inputMode={useRecovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            placeholder={useRecovery ? "XXXX-XXXX" : "000000"}
            aria-invalid={errors.code ? "true" : "false"}
            {...register("code")}
            className={cn(inputCls, errors.code && "border-red-400")}
          />
          {errors.code && (
            <p role="alert" className="text-sm text-red-500">
              {errors.code.message}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v);
            reset({ code: "" });
          }}
          className="self-start text-sm text-brand-primary hover:underline"
        >
          {useRecovery
            ? t("mfa.useAuthenticatorCode")
            : t("mfa.useRecoveryCode")}
        </button>

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="w-full rounded-lg py-2.5"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("login.signingIn")}
            </>
          ) : (
            t("mfa.confirmButton")
          )}
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-gray-600"
        >
          {t("mfa.backToLogin")}
        </button>
      </form>
    </div>
  );
}
